import { AiSummaryCard } from "./AiSummaryCard";
import { CandidacyExperienceSection } from "./CandidacyExperienceSection";
import { CandidacyLogisticsSection } from "./CandidacyLogisticsSection";
import { CandidacyStevieCallSection } from "./CandidacyStevieCallSection";
import { StevieCallRecommendation } from "./StevieCallRecommendation";
import { withRequiredFields } from "@/components/user-sheet/withRequiredFields";

import { Column, ErrorText, LoadingIndicator } from "@/components";

import { useUserSheet } from "@/components/user-sheet/userSheetContext";
import { useGetCallHistory } from "@/hooks/queries/useGetCallHistory";
import { STEVIE_TAB_JOB_ID } from "@/lib/constants";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { cn } from "@/lib/utils";
import { ClassifiedKeyword } from "@/types";

export const CandidacyTab = ({
  highlightKeywords,
  onOpenSheetTab,
  onOpenStevieCalls,
}: {
  highlightKeywords?: ClassifiedKeyword[];
  onOpenSheetTab?: (tab: "notes" | "records") => void;
  // Jumps to the Stevie tab. The recommendation badge next to the link stays
  // with the pilot job for now: it is a product statement, not call data.
  onOpenStevieCalls?: () => void;
}) => {
  /* Context */
  const { candidacyId, contentPaddingClassName, jobId, workerId } =
    useUserSheet();

  /* Database CRUD Operations */
  // Read
  const candidacyInfo = useGetCandidacyInfo({
    candidacyId: candidacyId ?? "",
  }).data;
  const userApi = useGetCandidacySheetData({ jobId, userId: workerId });
  const callHistoryQuery = useGetCallHistory(workerId);

  /* Helpers */
  const aiSummary = candidacyInfo?.aiSummary;
  const profile = userApi.data;
  const candidateFirstName = profile?.profile.firstName ?? "Candidate";
  const workExperience = profile?.workExperience;

  /* Render */
  const renderExperience = () => {
    if (userApi.isError) {
      return <ErrorText errorMessage="Error retrieving profile." />;
    }
    if (!workExperience) return null;

    return (
      <CandidacyExperienceSection
        highlightKeywords={highlightKeywords}
        workExperiences={withRequiredFields(workExperience, ["id"])}
      />
    );
  };

  const render = () => {
    // One spinner for the whole tab while the profile and call history load —
    // per-section pending states would otherwise stack multiple spinners.
    if (userApi.isPending || callHistoryQuery.isPending) {
      return (
        <Column className={cn("py-7", contentPaddingClassName)}>
          <LoadingIndicator variant="medium" />
        </Column>
      );
    }

    return (
      <Column className={cn("mb-12 gap-0 py-6", contentPaddingClassName)}>
        {aiSummary ? <AiSummaryCard summary={aiSummary} /> : null}
        <Column className="pt-10">
          <CandidacyLogisticsSection onOpenSheetTab={onOpenSheetTab} />
        </Column>
        <Column className="pt-10">
          <CandidacyStevieCallSection
            aiCalls={callHistoryQuery.data?.aiCalls ?? []}
            candidateFirstName={candidateFirstName}
            isError={callHistoryQuery.isError}
            onOpenSheetTab={onOpenSheetTab}
            onViewOtherCalls={onOpenStevieCalls}
            titleActions={
              onOpenStevieCalls ? (
                <StevieCallRecommendation
                  hideRecommendation={jobId !== STEVIE_TAB_JOB_ID}
                  onOpenStevieCalls={onOpenStevieCalls}
                />
              ) : undefined
            }
          />
        </Column>
        <Column className="pt-10">{renderExperience()}</Column>
      </Column>
    );
  };

  return render();
};
